(() => {
  const script = document.currentScript;
  const hardwareKeys = new Set(['case', 'mobo', 'cpu', 'ram', 'gpu', 'psu', 'cooler', 'wifi', 'sata_ssd', 'nvme_ssd', 'sata_hdd']);
  const accessoryKinds = new Set(['monitors','keyboard','keyboard_mouse','mouse','headset','headphones','earphones','loudspeaker','microphone','mouse_pad','desk_pad','webcam','wrist_rest','gaming_controller']);
  const money = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' });
  const inStock = option => option.availability?.available === true && (option.availability.quantity == null || option.availability.quantity > 0);
  const price = option => Number.isFinite(option.price?.amount) && option.price.amount >= 0 ? option.price.amount : Infinity;
  const selectionKey = selection => JSON.stringify([Object.entries(selection.selections).sort(([a], [b]) => a.localeCompare(b)), selection.accessories]);
  const copySelection = selection => ({ selections: { ...selection.selections }, accessories: selection.accessories.map(item => ({ ...item })) });
  const confirmed = result => result?.success === true && result.compatible === true && result.checkoutReady === true
    && result.price?.currency === 'GBP' && Number.isFinite(result.price.amount) && result.price.amount > 0;
  const stockGroups = catalog => (catalog?.componentGroups || []).filter(group => hardwareKeys.has(group.key)
    && group.visible !== false && group.sendToShopify !== false && Array.isArray(group.options)).map(group => ({ ...group,
    options: [...new Map(group.options.map(option => [option.sku, option])).values()]
      .filter(option => inStock(option) && Number.isFinite(price(option))).sort((a, b) => price(a) - price(b) || a.sku.localeCompare(b.sku)),
  }));
  const partName = part => {
    let name = part.option.filterLabel || part.option.label;
    if (['ram', 'psu'].includes(part.key) && /^\d+(?:\.\d+)?$/.test(name.trim())) name += part.key === 'ram' ? 'GB' : 'W';
    return name;
  };
  // Use the same named variants as the picker, with units on public specs.
  function reviewOption(key, option, listed) {
    if (listed?.variantId && option.variantId && String(listed.variantId) !== String(option.variantId)) listed = null;
    const specs = new Map();
    for (const spec of [...(listed?.displaySpecs || []), ...(option.displaySpecs || [])]) {
      const label = String(spec?.label || '').trim();
      const value = String(spec?.value ?? '').trim();
      if (!label || !value || /^(?:not listed|unknown|n\/?a)$/i.test(value) || /\b(?:EAN|SKU)\b/i.test(label)) continue;
      const id = label.toLowerCase();
      if (!specs.has(id)) specs.set(id, { label, value });
    }
    let label = String(listed?.label || option.label || option.sku).trim();
    const addSpec = (name, value, pattern) => {
      if (value && ![...specs.values()].some(spec => pattern.test(spec.label))) specs.set(name.toLowerCase(), { label: name, value });
    };
    if (key === 'psu' && /^\d+(?:,\d{3})*(?:\.\d+)?\s*W?$/i.test(label)) label = label.replace(/\s*W?$/i, 'W');
    if (key === 'ram' && /^\d+(?:\.\d+)?$/.test(label)) label += 'GB';
    if (key === 'gpu') {
      if (!/\bNVIDIA\b/i.test(label) && /\b(?:GeForce|RTX|GTX|GT\s*\d{3,4})\b/i.test(label)) {
        label = /\bGeForce\b/i.test(label) ? label.replace(/\bGeForce\b/i, 'NVIDIA GeForce') : label.replace(/\b(?=RTX|GTX|GT\s*\d)/i, 'NVIDIA GeForce ');
      } else if (!/\bAMD\b/i.test(label) && /\bRadeon\b/i.test(label)) label = label.replace(/\bRadeon\b/i, 'AMD Radeon');
      else if (!/\bIntel\b/i.test(label) && /\bArc\s+[AB]\d{3}\b/i.test(label)) label = label.replace(/\bArc\b/i, 'Intel Arc');
      const capacity = label.match(/\b(\d+(?:\.\d+)?)\s*GB\b/i)?.[1];
      addSpec('Video memory', capacity ? `${capacity}GB` : '', /^(?:Video memory|VRAM|(?:GPU )?Memory (?:size|capacity))$/i);
    }
    if (['sata_ssd', 'nvme_ssd'].includes(key)) addSpec('Interface', key === 'nvme_ssd' ? 'NVMe' : 'SATA', /interface/i);
    if (key === 'wifi') {
      addSpec('Interface', /\bPCI Express\b/i.test(label) ? 'PCI Express' : /\bUSB\b/i.test(label) ? 'USB' : '', /interface/i);
      addSpec('Wireless speed', label.match(/\b\d+(?:\.\d+)?\s*(?:Mbit\/s|Mbps|Gbit\/s)\b/i)?.[0], /speed|data rate/i);
    }
    const units = [
      [/wattage|^TDP(?: Rating)?(?: \(W\))?$|thermal design power/i, 'W'],
      [/\(GB\)$/i, 'GB'], [/\(MB\)$/i, 'MB'], [/\(mm\)$/i, 'mm'], [/\(kg\)$/i, 'kg'],
      [/\(bit\)$/i, 'bit'], [/\(RPM\)$|^RPM$/i, 'RPM'], [/\(MB\/s\)$/i, 'MB/s'],
      [/^(?:Processor Speed \(Base\)|Base (?:Speed|Clock)|Boost (?:Speed|Clock))$/i, 'GHz'],
    ];
    const names = { 'PSU Wattage': 'Power', 'Efficiency Rating': 'Efficiency', 'Memory Bus Width (bit)': 'Memory bus',
      'Memory Type': 'Memory type', 'Video memory': 'Video memory',
      'Processor Speed (Base)': 'Base clock', 'Boost Speed': 'Boost clock', 'CPU Socket Type': 'CPU socket',
      'Motherboard Form Factor': 'Form factor', 'DDR Type': 'Memory type', 'Total Capacity (GB)': 'Capacity',
      'Sequential Read (MB/s)': 'Read speed', 'Sequential Write (MB/s)': 'Write speed',
      'Rotational Speed (RPM)': 'Spindle speed', 'Cache (MB)': 'Cache', 'CPU Cooler Height (mm)': 'Height',
      'Supported Processor Sockets': 'Supported sockets', 'Length (mm)': 'Length', 'Max Resolution': 'Max resolution' };
    const priority = {
      case: /case type|colour|supported motherboard|max gpu|max cpu cooler|front i\/o/i,
      mobo: /cpu socket|chipset|form factor|ddr|wi-fi|bluetooth/i,
      cpu: /socket|core|thread|speed|clock|tdp/i, ram: /total capacity|generation|modules|speed|latency/i,
      gpu: /video memory|vram|memory (?:capacity|size|type|bus)|length|max resolution/i,
      psu: /wattage|efficiency|modularity|form factor/i,
      cooler: /cooler type|supported.*socket|height|tdp|radiator|fan/i,
      sata_ssd: /capacity|interface|form factor|sequential/i, nvme_ssd: /capacity|interface|form factor|pcie|sequential/i,
      sata_hdd: /capacity|interface|rotational|cache/i, wifi: /wi-fi|wireless|speed|bluetooth|interface|antenna/i,
    }[key];
    const ordered = [...specs.values()].sort((a, b) => Number(!!priority?.test(b.label)) - Number(!!priority?.test(a.label)));
    const displaySpecs = ordered.map(spec => {
      const unit = units.find(([pattern]) => pattern.test(spec.label))?.[1];
      const value = unit && /^\d+(?:,\d{3})*(?:\.\d+)?$/.test(spec.value) ? `${spec.value}${unit === 'W' || unit === 'GB' ? '' : ' '}${unit}` : spec.value;
      return { label: names[spec.label] || spec.label.replace(/ \((?:GB|MB|mm|kg|bit|RPM|MB\/s|W)\)$/i, ''), value };
    });
    return { ...listed, ...option, label, displaySpecs, componentKey: key, images: listed?.images?.length ? listed.images : option.images };
  }

  // These are complete builds checked by the server, including stock and price.
  // Selecting one is a local lookup, never a new compatibility assumption.
  function checkedBuildStore() {
    let state = null, timer = null, pending = null;
    const diagnostic = (key,value) => {if(document.documentElement)document.documentElement.dataset['pcCheckedBuild'+key]=String(value);};
    diagnostic('State','initialising');
    const required = ['case','cpu','mobo','gpu','ram','psu','cooler','windows_key','kettle_lead'];
    const fresh = data => data?.version === 1 && typeof data.revision === 'string'
      && Number.isFinite(data.generatedAt) && Number.isFinite(data.expiresAt)
      && data.generatedAt <= Date.now() + 10000 && data.expiresAt > Date.now()
      && data.expiresAt > data.generatedAt && data.expiresAt - data.generatedAt <= 180000;
    const typed = value => ArrayBuffer.isView(value) && Object.prototype.toString.call(value) === '[object Int32Array]';
    function installPacked(source) {
      const data = source?.index, packed = source?.preparedRowsV1, repair = source?.preparedRepairIndexV1;
      if (!fresh(data) || (state && data.generatedAt <= state.generatedAt) || !Array.isArray(data.keys) || !Array.isArray(data.skus)
        || new Set(data.keys).size !== data.keys.length || new Set(data.skus).size !== data.skus.length
        || !Array.isArray(source.chunks) || source.chunks.length !== data.chunks?.length
        || source.chunks.some(chunk => chunk?.revision !== data.revision)
        || packed?.version !== 1 || packed.stride !== data.keys.length + 4 || !Array.isArray(packed.groups)
        || !packed.groups.length || packed.groups.some(group => !typed(group.data) || group.data.length % packed.stride)
        || repair?.version !== 1 || !typed(repair.refs) || !repair.refs.length || repair.refs.length % 2
        || !Array.isArray(repair.buckets) || repair.buckets.some(bucket => typeof bucket.key !== 'string' || !typed(bucket.data))) return false;
      const options = new Map(source.chunks.filter(chunk => chunk.kind === 'options')
        .flatMap(chunk => chunk.values || []).map(option => [option.componentKey + '|' + option.sku, option]));
      const products = new Set(source.chunks.filter(chunk => chunk.kind === 'products')
        .flatMap(chunk => chunk.values || []).filter(product => /^[a-z0-9][a-z0-9-]*$/.test(product.handle)).map(product => product.caseSku));
      state = { ...data, options, products, packed, refs: repair.refs,
        buckets: new Map(repair.buckets.map(bucket => [bucket.key, bucket.data])) };
      diagnostic('State','ready');diagnostic('Revision',state.revision);diagnostic('ExpiresAt',state.expiresAt);
      return true;
    }
    function* packedCandidates(selection, allowSpecificationChange, lockedKeys) {
      const ids = allowSpecificationChange ? null : state.buckets.get(JSON.stringify([selection.selections.ram, selection.selections.gpu]));
      const length = allowSpecificationChange ? state.refs.length / 2 : ids?.length || 0;
      const locks = lockedKeys.map(key => [state.keys.indexOf(key), selection.selections[key]]);
      for (let position = 0; position < length; position++) {
        const ref = allowSpecificationChange ? position : ids[position];
        if (!Number.isInteger(ref) || ref < 0 || ref * 2 + 1 >= state.refs.length) continue;
        const bucket = state.packed.groups[state.refs[ref * 2]], offset = state.refs[ref * 2 + 1];
        if (!bucket || !Number.isInteger(offset) || offset < 0 || offset % state.packed.stride
          || offset + state.packed.stride > bucket.data.length
          || locks.some(([index, sku]) => (index >= 0 && bucket.data[offset + index] >= 0 ? state.skus[bucket.data[offset + index]] : undefined) !== sku)) continue;
        const values = bucket.data.subarray(offset, offset + state.keys.length), pennies = bucket.data[offset + state.keys.length], status = bucket.data[offset + state.keys.length + 1];
        if (!Number.isSafeInteger(pennies) || pennies <= 0 || ![0, 1].includes(status)
          || values.some(id => id < -1 || id >= state.skus.length)) continue;
        const parts = Object.fromEntries(state.keys.flatMap((key, index) => values[index] >= 0 ? [[key, state.skus[values[index]]]] : []));
        if (required.some(key => !parts[key]) || !state.products.has(parts.case) || bucket.caseSku !== parts.case
          || Object.entries(parts).some(([key, sku]) => !inStock(state.options.get(key + '|' + sku) || {}))
          || !allowSpecificationChange && (parts.ram !== selection.selections.ram || parts.gpu !== selection.selections.gpu)) continue;
        yield { parts, pennies, compatibilityStatus: status === 1 ? 'review' : 'compatible' };
      }
    }
    function install(data) {
      if (!fresh(data) || !Array.isArray(data.keys) || !Array.isArray(data.skus)
        || !Array.isArray(data.rows) || !Array.isArray(data.options) || !Array.isArray(data.products)
        || new Set(data.keys).size !== data.keys.length || new Set(data.skus).size !== data.skus.length
        || (state && data.generatedAt <= state.generatedAt)) return false;
      const options = new Map(data.options.map(option => [option.componentKey+'|'+option.sku,option]));
      const products = new Set(data.products.filter(product => /^[a-z0-9][a-z0-9-]*$/.test(product.handle)).map(product => product.caseSku));
      const buckets = new Map(), all = [];
      for (const row of data.rows) {
        if (!Array.isArray(row?.[0]) || row[0].length !== data.keys.length
          || !Number.isSafeInteger(row[1]) || row[1] <= 0 || ![0,1].includes(row[2])
          || row[0].some(id => !Number.isInteger(id) || id < -1 || id >= data.skus.length)) continue;
        const parts = Object.fromEntries(data.keys.flatMap((key,i) => row[0][i] >= 0 ? [[key,data.skus[row[0][i]]]] : []));
        if (required.some(key => !parts[key]) || !products.has(parts.case)
          || Object.entries(parts).some(([key,sku]) => !inStock(options.get(key+'|'+sku) || {}))) continue;
        const signature = JSON.stringify([parts.ram,parts.gpu]);
        if (!buckets.has(signature)) buckets.set(signature,[]);
        const checked = {parts,pennies:row[1],compatibilityStatus:row[2] === 1 ? 'review' : 'compatible'};
        buckets.get(signature).push(checked);all.push(checked);
      }
      if (!buckets.size) return false;
      for (const rows of buckets.values()) rows.sort((a,b) => a.pennies-b.pennies);
      state = {version:1,revision:data.revision,generatedAt:data.generatedAt,expiresAt:data.expiresAt,options,buckets,all};
      diagnostic('State','ready');diagnostic('Revision',state.revision);diagnostic('ExpiresAt',state.expiresAt);
      window.ElitePCPrepared?.install?.(data);
      return true;
    }
    try {
      const source = window.ElitePCData?.payload || JSON.parse(document.querySelector('[data-elite-pc-prepared]')?.textContent || 'null');
      if(window.ElitePCPrepared?.decodePublication?.(source)===false)throw Error('Invalid row encoding');
      const index = source?.index;
      if (!installPacked(source) && Array.isArray(source?.chunks) && source.chunks.length === index?.chunks?.length
        && source.chunks.every(chunk => chunk?.revision === index.revision)) {
        install({...index,rows:source.chunks.filter(chunk => chunk.kind === 'rows').flatMap(chunk => Object.values(chunk.values || {}).flat()),
          options:source.chunks.filter(chunk => chunk.kind === 'options').flatMap(chunk => chunk.values || []),
          products:source.chunks.filter(chunk => chunk.kind === 'products').flatMap(chunk => chunk.values || [])});
      }
    } catch (_) { /* A malformed or expired snapshot cannot approve a repair. */ }
    if(!state)diagnostic('State','missing-or-expired');
    function schedule(delay) {
      clearTimeout(timer);
      timer = setTimeout(refresh, delay ?? Math.max(5000, (state?.expiresAt || Date.now()) - Date.now() - 70000));
      timer?.unref?.();
    }
    async function refresh() {
      if (pending) return pending;
      if (document.hidden || typeof navigator !== 'undefined' && (navigator.onLine === false || navigator.connection?.saveData)) {
        schedule(15000); return;
      }
      const controller = new AbortController(), timeout = setTimeout(() => controller.abort(),10000);
      pending = (async () => {
        try {
          diagnostic('Refresh','requesting');
          const packed = await window.ElitePCData?.loadPrepared?.(controller.signal);
          let installed;
          if(packed){
            installed=installPacked(packed);
            if(installed)window.ElitePCPrepared?.install?.(packed);
          }else{
            const response = await fetch('/apps/pc-configurator?action=prepared',{cache:'no-store',headers:{Accept:'application/json'},signal:controller.signal});
            if (!response.ok) throw Error('HTTP '+response.status);
            installed = install(await response.json());
          }
          diagnostic('Refresh',installed?'updated':'unchanged');
          schedule(installed ? undefined : 15000);
        } catch (error) { diagnostic('Refresh',error.name==='AbortError'?'timed-out':error.message);schedule(15000); }
        finally { clearTimeout(timeout); pending = null; }
      })();
      return pending;
    }
    document.addEventListener?.('visibilitychange',() => {
      if (!document.hidden && (!state || state.expiresAt-Date.now()<70000)) void refresh();
    });
    // This refresh is background work. The click path never starts or awaits it.
    schedule(state ? undefined : 1000);
    return {
      candidates(selection,minimumRemainingMs,allowSpecificationChange=false,lockedKeys=[]) {
        if (!fresh(state) || state.expiresAt <= Date.now()+minimumRemainingMs || selection.accessories?.length) return [];
        const rows=state.packed ? [...packedCandidates(selection,allowSpecificationChange,lockedKeys)]
          : allowSpecificationChange?state.all:state.buckets.get(JSON.stringify([selection.selections.ram,selection.selections.gpu])) || [];
        return rows.filter(row=>lockedKeys.every(key=>row.parts[key]===selection.selections[key])).map(row => ({
          selection:{selections:{...row.parts},accessories:[]},expiresAt:state.expiresAt,source:'prepared-catalogue',options:state.options,
          validation:{success:true,compatible:true,checkoutReady:true,checkoutMode:'draft_order',checkoutUrl:null,cartPayload:null,quantity:1,
            componentSkus:{...row.parts},build:{allComponentsInStock:true,stockStatus:'in_stock'},compatibilityStatus:row.compatibilityStatus,compatibilityWarnings:[],
            preparedRevision:state.revision,price:{amount:row.pennies/100,pennies:row.pennies,totalAmount:row.pennies/100,currency:'GBP',formatted:money.format(row.pennies/100),pcPricingApplied:true}}}));
      },
      refresh,
    };
  }
  // Product pickers need repair candidates immediately. Listing pages have no
  // picker: wait until a cart repair requests them before decoding every
  // checked combination in the collection-wide publication.
  let checkedBuilds = !window.ElitePCData?.pending && window.location?.pathname?.startsWith('/products/') ? checkedBuildStore() : null;
  // One preparation covers both actions for every card in this exact build.
  // Reading a prepared action never fetches or starts a compatibility search.
  const pickerMatrices = new Map();
  let activePickerMatrix = null, pickerMatrixTimer = null;
  let installedFirstVisitRevision = null;
  const matrixKey = selection => JSON.stringify([Object.entries(selection.selections || {}).sort(([a],[b])=>a.localeCompare(b)),
    (selection.accessories || []).map(line=>[line.component,line.sku,String(line.variantId)]).sort((a,b)=>JSON.stringify(a).localeCompare(JSON.stringify(b)))]);
  function installFirstVisit() {
    try {
      const source=JSON.parse(document.querySelector('[data-elite-pc-first-visit]')?.textContent||'null');
      if(source?.revision && source.revision===installedFirstVisitRevision)return;
      const prepared=window.ElitePCPrepared;
      if(source?.version!==1||!Array.isArray(source.keys)||!Array.isArray(source.entries)||source.entries.length>3
        ||!prepared?.alive?.()||source.catalogRevision!==prepared.revision||!Number.isFinite(source.generatedAt)||!Number.isFinite(source.expiresAt)
        ||source.generatedAt>Date.now()+10000||source.expiresAt<=Date.now()+500||source.expiresAt<=source.generatedAt
        ||source.expiresAt-source.generatedAt>180000||new Set(source.keys).size!==source.keys.length)return;
      const readParts=(ids,skus)=>{
        if(!Array.isArray(ids)||ids.length!==source.keys.length||ids.some(id=>!Number.isInteger(id)||id < -1||id>=skus.length))throw Error('Invalid selections');
        const values=Object.fromEntries(source.keys.flatMap((key,i)=>ids[i]>=0?[[key,skus[ids[i]]]]:[]));
        if(['case','mobo','cpu','ram','gpu','psu','cooler','windows_key','kettle_lead'].some(key=>!values[key])
          ||Object.entries(values).some(([key,sku])=>!inStock(prepared.option(key,sku)||{})))throw Error('Unstocked selections');
        return values;
      };
      const price=pennies=>Number.isSafeInteger(pennies)&&pennies>0?{amount:pennies/100,pennies,currency:'GBP',formatted:money.format(pennies/100),pcPricingApplied:true,pricingRevision:source.pricingRevision}:null;
      for(const entry of source.entries) {
        if(!Array.isArray(entry.skus)||!Array.isArray(entry.rows)||!Array.isArray(entry.builds)||!Array.isArray(entry.reasons))continue;
        const selection={selections:readParts(entry.base,entry.skus),accessories:[]};
        if(selection.selections.case!==source.caseSku)continue;
        const key=matrixKey(selection),rows=new Map();
        for(const row of entry.rows) {
          const component=source.keys[row[0]],sku=entry.skus[row[1]],value=price(row[3]);
          if(!hardwareKeys.has(component)||!sku||![-1,0,1].includes(row[2])||!Array.isArray(row[4])||!inStock(prepared.option(component,sku)||{}))throw Error('Invalid row');
          const original={...selection.selections,[component]:sku};
          const action=(buildId,mode)=>{
            if(buildId===-1||buildId===-2)return {state:buildId===-1?'unavailable':'not-needed',expiresAt:source.expiresAt};
            const build=entry.builds[buildId];if(!Number.isInteger(buildId)||!build)throw Error('Invalid repair');
            const after=readParts(build[0],entry.skus),total=price(build[1]);
            if(!total||Object.keys(after).sort().join('|')!==Object.keys(original).sort().join('|'))throw Error('Invalid repair build');
            const changed=Object.keys(after).filter(key=>after[key]!==original[key]);
            if(!changed.length||(mode==='keep'?changed.includes(component):changed.length!==1||changed[0]!==component))throw Error('Wrong repair action');
            const labels={case:'Case',mobo:'Motherboard (MOBO)',cpu:'Processor (CPU)',ram:'Memory (RAM)',gpu:'Graphics Card (GPU)',psu:'Power Supply Unit (PSU)',cooler:'CPU cooler',wifi:'Wi-Fi',nvme_ssd:'NVMe SSD',sata_ssd:'SATA SSD',sata_hdd:'Hard drive'};
            const changes=changed.map(key=>({key,label:labels[key]||key,option:prepared.option(key,after[key])}));
            if(changes.some(change=>!hardwareKeys.has(change.key)||!change.option))throw Error('Invalid repair changes');
            return {state:'ready',expiresAt:source.expiresAt,repair:{selection:{selections:after,accessories:[]},changes,
              validation:{success:true,compatible:true,checkoutReady:true,checkoutMode:'draft_order',checkoutUrl:null,cartPayload:null,quantity:1,
                componentSkus:{...after},build:{allComponentsInStock:true,stockStatus:'in_stock'},compatibilityStatus:build[2]===1?'review':'compatible',
                compatibilityWarnings:[],price:{...total,totalAmount:total.amount},preparedRevision:source.revision}}};
          };
          rows.set(component+'|'+sku,{component,sku,keep:action(row[6],'keep'),alternative:action(row[7],'alternative'),
            fit:{sku,status:row[2]===1?'compatible':row[2]===-1?'incompatible':'unknown',compatibilityStatus:row[2]===1?(row[5]===1?'review':'compatible'):row[2]===-1?'incompatible':'unknown',
              reasons:row[4].map(id=>entry.reasons[id]).filter(reason=>typeof reason==='string'),...(value?{price:value}:{}),expiresAt:source.expiresAt}});
        }
        const existing=pickerMatrices.get(key);
        if(!existing?.rows.size || existing.expiresAt<source.expiresAt) {
          const job=existing||{pending:false};
          Object.assign(job,{key,selection,rows,expiresAt:source.expiresAt,complete:true,revision:source.revision,proofRevision:source.proofRevision,firstVisit:true});
          pickerMatrices.set(key,job);
        }
      }
      installedFirstVisitRevision=source.revision;
      if(script?.dataset){script.dataset.pickerInlineEntries=String(pickerMatrices.size);if(pickerMatrices.size)script.dataset.pickerInlineReadyMs=String(Math.round(performance.now()));}
    } catch (_) { /* Incomplete, wrong-revision or expired data cannot approve a fix. */ }
  }
  installFirstVisit();
  document.addEventListener?.('elite-pc:data-ready',installFirstVisit,{once:true});
  // This asset loads asynchronously in the head. The published prices can
  // arrive later in the HTML; try again before the configurator needs them.
  document.addEventListener?.('DOMContentLoaded',installFirstVisit,{once:true});
  function pickerMatrixDiagnostic(job) {
    if (!script?.dataset) return;
    script.dataset.pickerMatrixState = job.error ? 'retrying' : job.complete ? 'ready' : 'preparing';
    script.dataset.pickerMatrixRows = String(job.rows.size);
    script.dataset.pickerMatrixReady = String([...job.rows.values()].reduce((n,row)=>n+['keep','alternative'].filter(mode=>row[mode]?.state==='ready').length,0));
    script.dataset.pickerMatrixExpiresAt = String(job.expiresAt || 0);
    script.dataset.pickerMatrixSource = job.firstVisit ? 'initial-page' : 'background';
    if(job.complete)script.dataset.pickerMatrixReadyMs ||= String(Math.round(performance.now()));
  }
  function matrixUpdated(job) {
    pickerMatrixDiagnostic(job);
    if(window.CustomEvent)window.dispatchEvent?.(new window.CustomEvent('elite:repair-updated'));
  }
  function schedulePickerMatrix(delay) {
    clearTimeout(pickerMatrixTimer);
    pickerMatrixTimer = setTimeout(refreshPickerMatrix,delay);
    pickerMatrixTimer?.unref?.();
  }
  async function refreshPickerMatrix() {
    const job = activePickerMatrix;
    if (!job || job.pending) return;
    if (!job.isCurrent() || document.hidden || navigator.onLine===false) { schedulePickerMatrix(2000);return; }
    const api=window.ElitePCPrivate;if(!api?.request){schedulePickerMatrix(500);return;}
    job.pending=true;
    const started=Date.now();
    const controller=new AbortController(),timeout=setTimeout(()=>controller.abort(),8000);
    try {
      const result=await api.request('picker-repairs',{...copySelection(job.selection),priorityComponents:job.priority},{signal:controller.signal});
      if(activePickerMatrix!==job || !job.isCurrent())return;
      if(result?.version!==1 || result.success!==true || matrixKey(result.selection)!==job.key || !Array.isArray(result.rows))throw Error('Invalid preparation');
      const expiresAt=Math.min(Number(result.expiresAt)||0,started+Math.max(0,Number(result.maxAgeMs)||0));
      if(expiresAt<=Date.now()+500)throw Error('Preparation expired');
      // Keep an unexpired completed check while the exact same verified inputs
      // are being renewed. A changed stock/price/rule fingerprint still revokes
      // all old rows immediately; renewal never extends their original expiry.
      const sameProof=typeof result.proofRevision==='string'&&result.proofRevision.length>0&&result.proofRevision===job.proofRevision;
      if(job.revision!==result.revision&&!sameProof || job.expiresAt<=Date.now())job.rows.clear();
      for(const row of result.rows) {
        if(!hardwareKeys.has(row?.component) || typeof row.sku!=='string')continue;
        const previous=job.rows.get(row.component+'|'+row.sku);
        const next={component:row.component,sku:row.sku};
        if(row.fit?.sku===row.sku && ['compatible','incompatible','unknown'].includes(row.fit.status)) {
          next.fit={...row.fit,expiresAt,checkedAt:started};
        } else if(previous?.fit?.expiresAt>Date.now()+500)next.fit=previous.fit;
        for(const mode of ['keep','alternative']) {
          const action=row[mode];
          if(!['ready','unavailable','not-needed','pending'].includes(action?.state))continue;
          next[mode]=action.state==='pending'&&previous?.[mode]?.expiresAt>Date.now()+500
            ? previous[mode] : {...action,expiresAt,checkedAt:started};
        }
        job.rows.set(row.component+'|'+row.sku,next);
      }
      job.revision=result.revision;job.proofRevision=result.proofRevision;job.expiresAt=expiresAt;job.complete=result.complete===true;job.error=false;job.firstVisit=false;
      matrixUpdated(job);schedulePickerMatrix(job.complete?Math.max(1000,Math.min(10000,expiresAt-Date.now()-12000)):500);
    } catch (_) {
      if(activePickerMatrix===job){job.error=true;matrixUpdated(job);schedulePickerMatrix(2000);}
    } finally {clearTimeout(timeout);job.pending=false;}
  }
  function preparePickerBuild({selection,priorityComponents=[],isCurrent=()=>true}) {
    installFirstVisit();
    const key=matrixKey(selection);
    let job=pickerMatrices.get(key);
    if(!job){job={key,selection:copySelection(selection),rows:new Map(),expiresAt:0,complete:false,pending:false};pickerMatrices.set(key,job);}
    while(pickerMatrices.size>4)pickerMatrices.delete(pickerMatrices.keys().next().value);
    job.isCurrent=isCurrent;job.priority=[...new Set(priorityComponents)].filter(key=>hardwareKeys.has(key));
    const changed=activePickerMatrix!==job;activePickerMatrix=job;
    pickerMatrixDiagnostic(job);
    if(changed || !pickerMatrixTimer)schedulePickerMatrix(job.firstVisit?Math.max(1000,Math.min(10000,job.expiresAt-Date.now()-30000)):0);
    return {state:job.complete&&job.expiresAt>Date.now()?'ready':'pending',rows:job.rows.size};
  }
  function initialPickerSelection() {
    if(!window.location?.href)return null;
    const url=new window.URL(window.location.href);
    if(!url.pathname.startsWith('/products/'))return null;
    const allowed=new Set([...hardwareKeys,'windows_key','kettle_lead']),selections={};
    for(const [key,value]of url.searchParams) {
      if(!/^(elite_|component_)/.test(key))continue;
      const component=key.replace(/^(elite_|component_)/,'');
      // Accessories require their exact variant identities from the catalogue.
      // Incomplete or noncanonical links use the normal complete-build hook.
      if(!allowed.has(component)||!value||value.startsWith('__')||selections[component])return null;
      selections[component]=value;
    }
    if(!Object.keys(selections).length&&(!url.searchParams.has('pc')||url.searchParams.get('pc')==='gaming-pc')) {
      for(const job of pickerMatrices.values()) {
        const product=window.ElitePCPrepared?.catalog?.(job.selection.selections.case)?.product;
        if(product?.handle&&url.pathname.replace(/\/$/,'')==='/products/'+product.handle)return copySelection(job.selection);
      }
    }
    if(['case','mobo','cpu','ram','gpu','psu','cooler','windows_key','kettle_lead'].some(key=>!selections[key])
      ||!selections.nvme_ssd&&!selections.sata_ssd)return null;
    return {selections,accessories:[]};
  }
  function matrixPicker(options,minimumRemainingMs=500) {
    const component=options.component,mode=options.repairMode==='alternative'?'alternative':'keep';
    if(!activePickerMatrix || !component)return null;
    const before=options.selection;
    // Selecting the card makes it the new draft. Its already-checked action
    // still describes that exact candidate, even as the next build prepares.
    // Only the card's component can differ from the source build; every other
    // part and accessory must match. A newer terminal answer takes precedence.
    const matching=[...pickerMatrices.values()].filter(job=>{
      const parts={...before.selections};
      if(job.selection.selections[component])parts[component]=job.selection.selections[component];
      else delete parts[component];
      return matrixKey({...before,selections:parts})===job.key;
    });
    if(!matching.length)return null;
    const actions=matching.map(job=>job.rows.get(component+'|'+before.selections[component])?.[mode])
      .filter(action=>action?.expiresAt>Date.now()+minimumRemainingMs)
      .sort((a,b)=>(b.checkedAt||0)-(a.checkedAt||0));
    const action=actions.find(action=>action.state!=='pending') || actions[0];
    if(!action || action.expiresAt<=Date.now()+minimumRemainingMs)return {state:'pending'};
    if(action.state!=='ready')return {state:action.state==='unavailable'?'unavailable':'pending'};
    const request=pickerRepairOptions(options),repair=eligibleRepairs({suggestions:[action.repair]},request)[0];
    if(!repair || !confirmed(repair.validation)||repair.validation.build?.allComponentsInStock!==true)return {state:'pending'};
    // A later live stock/fit failure overrides an older prepared outcome.
    const newer=window.ElitePCQuoteTransport?.peekLive?.(repair.selection);
    if(newer&&(!confirmed(newer.validation)||newer.validation.build?.allComponentsInStock!==true))return {state:'pending'};
    return {state:'ready',repair:{...namedRepair(repair,options.catalog,action.expiresAt),usedAlternative:mode==='alternative',repairMode:mode,checkedSource:'prepared-picker-matrix'}};
  }
  function preparedPickerFit(selections,component,sku,minimumRemainingMs=0,accessories=[]) {
    const fits=[];
    for(const job of pickerMatrices.values()) {
      const parts={...selections};
      if(job.selection.selections[component])parts[component]=job.selection.selections[component];else delete parts[component];
      if(matrixKey({selections:parts,accessories})!==job.key)continue;
      const fit=job.rows.get(component+'|'+sku)?.fit;
      if(fit?.expiresAt>Date.now()+minimumRemainingMs)fits.push(fit);
    }
    const fit=fits.sort((a,b)=>b.checkedAt-a.checkedAt)[0];
    if(!fit)return null;
    const {expiresAt,checkedAt,...answer}=fit;
    return answer;
  }
  function preparedPickerPrice(selection,accessories=[]) {
    for(const component of hardwareKeys) {
      const fit=preparedPickerFit(selection,component,selection[component],0,accessories);
      if(fit?.price?.currency==='GBP'&&Number.isFinite(fit.price.amount)&&fit.price.pcPricingApplied===true)return fit.price;
    }
    return null;
  }
  function preparedPickerDisplayPrice(selections,accessories=[]) {
    let newest=null;
    for(const job of pickerMatrices.values())for(const component of hardwareKeys) {
      const parts={...selections};
      if(job.selection.selections[component])parts[component]=job.selection.selections[component];else delete parts[component];
      if(matrixKey({selections:parts,accessories})!==job.key)continue;
      const fit=job.rows.get(component+'|'+selections[component])?.fit;
      // First-visit proofs last at most three minutes. Two extra minutes only
      // preserve the displayed total; this function returns no fit approval.
      if(fit?.expiresAt+120000>Date.now()&&fit.price?.currency==='GBP'&&Number.isFinite(fit.price.amount)
        &&fit.price.amount>0&&fit.price.pcPricingApplied===true&&(!newest||fit.expiresAt>newest.expiresAt))newest=fit;
    }
    return newest?{...newest.price}:null;
  }
  function checkedAlternative(options,minimumRemainingMs=500,allowSpecificationChange=false) {
    const before=options.selection,locked=options.lockedKeys || [],catalog=availableCatalog(options.catalog,before);
    const candidates=[...(window.ElitePCQuoteTransport?.checkedBuilds?.(minimumRemainingMs) || []),...(checkedBuilds ||= checkedBuildStore()).candidates(before,minimumRemainingMs,allowSpecificationChange,locked)];
    const accessorySignature=lines=>JSON.stringify((lines || []).map(line=>Object.entries(line).sort(([a],[b])=>a.localeCompare(b))).sort((a,b)=>JSON.stringify(a).localeCompare(JSON.stringify(b))));
    const originalKeys=Object.keys(before.selections).sort().join('|'),originalAccessories=accessorySignature(before.accessories);
    let best=null;
    for (const candidate of candidates) {
      const after=candidate.selection;
      if (!after?.selections || !confirmed(candidate.validation) || candidate.validation.build?.allComponentsInStock !== true
        || candidate.expiresAt <= Date.now()+minimumRemainingMs
        || Object.keys(after.selections).sort().join('|') !== originalKeys
        || accessorySignature(after.accessories) !== originalAccessories
        || (allowSpecificationChange?locked:['ram','gpu',...locked]).some(key=>before.selections[key] !== after.selections[key])) continue;
      const changes=[];
      let rejected=false;
      for (const key of Object.keys(before.selections)) {
        const sku=after.selections[key],group=catalog?.componentGroups?.find(value=>value.key===key);
        const listed=group?.options?.find(option=>option.sku===sku),option=listed || candidate.options?.get(key+'|'+sku);
        // A newer stock result can revoke an otherwise valid published build.
        if (listed && !inStock(listed)) {rejected=true;break;}
        if (sku===before.selections[key]) continue;
        if (!hardwareKeys.has(key) || !option || !inStock(option)) {rejected=true;break;}
        changes.push({key,label:group?.label || key,option});
      }
      if (rejected || !changes.length) continue;
      const newer=window.ElitePCQuoteTransport?.peekLive?.(after);
      if (newer && (!confirmed(newer.validation) || newer.validation.build?.allComponentsInStock !== true)) continue;
      const validation=newer?.validation || candidate.validation;
      const expiresAt=Math.min(candidate.expiresAt,newer?.expiresAt || Infinity);
      if (expiresAt <= Date.now()+minimumRemainingMs) continue;
      const suggestion={selection:copySelection(after),changes,validation,validationExpiresAt:expiresAt,checkedSource:candidate.source || 'shared-quote'};
      if (!eligibleRepairs({ suggestions: [suggestion] }, options).length) continue;
      if (!best || changes.length<best.changes.length || changes.length===best.changes.length && validation.price.amount<best.validation.price.amount) best=suggestion;
    }
    if(best)return namedRepair(best,catalog,best.validationExpiresAt);
    // Only Make compatible may lower specifications, and only after the
    // original RAM/GPU identity has no ready checked repair. Every candidate
    // remains a complete server-validated build with its original expiry.
    return !allowSpecificationChange&&options.repairMode==='keep'&&locked.includes(options.component)
      ? checkedAlternative(options,minimumRemainingMs,true) : null;
  }

  function reusableSuggestion(suggestion, signature, expiresAt) {
    const remaining = expiresAt - Date.now();
    return confirmed(suggestion.validation) && selectionKey(suggestion.selection) === signature
      && Number.isFinite(remaining) && remaining > 0 && remaining <= 15000;
  }
  async function request(action, selection, timeout = 5000) {
    if (timeout <= 0) throw new Error('The check took too long.');
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeout);
    try {
      const response = await fetch(action === 'catalog'
        ? `/apps/pc-configurator?action=catalog&caseSku=${encodeURIComponent(selection.selections.case || '')}&release=live-components-v2`
        : '/apps/pc-configurator?action=validate', {
        cache: 'no-store', headers: { Accept: 'application/json', ...(action === 'validate' ? { 'Content-Type': 'application/json' } : {}) },
        ...(action === 'validate' ? { method: 'POST', body: JSON.stringify({ ...selection, checkout: false, freshCheck: true }) } : {}), signal: controller.signal,
      });
      const data = await response.json();
      if (!response.ok || data.success !== true) throw new Error(data.message || 'Replacement parts could not be checked.');
      if (data.price) data.price = window.ElitePCPriceAddition?.quote(data.price) || data.price;
      return data;
    } finally { clearTimeout(timer); }
  }

  const usableCatalog = catalog => Array.isArray(catalog?.componentGroups) && catalog.componentGroups.some(group => Array.isArray(group.options) && group.options.length);
  function candidates(catalog, selection, issueKeys = []) {
    if (!usableCatalog(catalog)) return undefined;
    const affected = new Set(issueKeys.filter(key => hardwareKeys.has(key)));
    const groups = catalog.componentGroups.filter(group => hardwareKeys.has(group.key) && selection.selections[group.key]);
    if (!affected.size) for (const group of groups) {
      const selected = group.options.find(option => option.sku === selection.selections[group.key]);
      if (selected && !inStock(selected)) affected.add(group.key);
    }
    if (!affected.size) return undefined;
    const queues = groups.filter(group => affected.has(group.key)).map(group => {
      const selected = group.options.find(option => option.sku === selection.selections[group.key]);
      const model = option => String(option?.filterLabel || option?.label || '').trim().toLowerCase();
      const distance = option => Number.isFinite(price(option)) && Number.isFinite(price(selected || {})) ? Math.abs(price(option) - price(selected)) : 0;
      return group.options.filter(option => option.sku !== selection.selections[group.key] && inStock(option))
        .sort((a,b) => Number(model(b) === model(selected)) - Number(model(a) === model(selected)) || distance(a) - distance(b) || a.sku.localeCompare(b.sku));
    });
    const result = [];
    // Only SKU identifiers leave the browser. The server decides whether an
    // option fits and meets the original RAM/GPU specification floor.
    while (result.length < 12 && queues.some(queue => queue.length)) for (const queue of queues) {
      const option = queue.shift();
      if (option && !result.includes(option.sku)) result.push(option.sku);
      if (result.length === 12) break;
    }
    return result;
  }
  function accessoryCandidates(catalog, selection) {
    const selected = new Set((selection.accessories || []).map(line => String(line.variantId)));
    const queues = [...new Set((selection.accessories || []).map(line => line.component))].filter(key => accessoryKinds.has(key)).map(key =>
      (catalog?.componentGroups?.find(group => group.key === key)?.options || [])
        .filter(option => inStock(option) && /^[1-9]\d*$/.test(String(option.variantId)) && !selected.has(String(option.variantId)))
        .sort((a,b) => (a.priceOrder || 0)-(b.priceOrder || 0) || a.sku.localeCompare(b.sku))
        .map(option => ({component:key,sku:option.sku,variantId:String(option.variantId)})));
    const result = [];
    while (result.length < 12 && queues.some(queue => queue.length)) for (const queue of queues) {
      const line = queue.shift(); if (line) result.push(line); if (result.length === 12) break;
    }
    return result;
  }
  function availableCatalog(supplied, selection) {
    if (usableCatalog(supplied)) return supplied;
    try {
      const inline = JSON.parse(document.querySelector('[data-elite-pc-catalog]')?.textContent || 'null');
      if (usableCatalog(inline)) return inline;
    } catch (_) { /* A missing snapshot does not block the live catalogue. */ }
    try {
      const prefix = `elite-pc-catalog:v6:${encodeURIComponent(selection.selections.case || '')}:`;
      for (let index = 0; index < sessionStorage.length; index++) {
        const key = sessionStorage.key(index);
        if (!key?.startsWith(prefix)) continue;
        const entry = JSON.parse(sessionStorage.getItem(key));
        const age = Date.now() - entry?.savedAt;
        if (age >= 0 && age < 5 * 60 * 1000 && usableCatalog(entry.catalog)) return entry.catalog;
      }
    } catch (_) { /* Storage is optional in private browser sessions. */ }
    return null;
  }
  function configurationUrl(selection, productUrl, catalog) {
    const url = new URL(productUrl || location.href, location.origin);
    if (url.origin !== location.origin || !url.pathname.startsWith('/products/')) {
      if (!catalog?.product?.handle) return '';
      url.href = new URL(`/products/${encodeURIComponent(catalog.product.handle)}`, location.origin).href;
    }
    for (const key of [...url.searchParams.keys()]) if (/^(elite_|component_)/.test(key)) url.searchParams.delete(key);
    url.searchParams.set('pc', 'gaming-pc-alternative');
    for (const [key, sku] of Object.entries(selection.selections)) url.searchParams.set(`elite_${key}`, sku);
    for (const item of selection.accessories) url.searchParams.append(`elite_${item.component}`, item.sku);
    return url.pathname + url.search;
  }
  async function productCatalog(productUrl, timeout) {
    if (!productUrl || timeout <= 0) return null;
    const url = new URL(productUrl, location.origin);
    if (url.origin !== location.origin || !url.pathname.startsWith('/products/')) return null;
    url.search = '';
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeout);
    try {
      const response = await fetch(url.href, { signal: controller.signal, headers: { Accept: 'text/html' } });
      if (!response.ok) return null;
      const page = new DOMParser().parseFromString(await response.text(), 'text/html');
      const catalog = JSON.parse(page.querySelector('[data-elite-pc-catalog]')?.textContent || 'null');
      return usableCatalog(catalog) ? catalog : null;
    } finally { clearTimeout(timer); }
  }

  // Add a small choice of alternatives using the existing private endpoints.
  // Only public product facts and the answer for this exact build reach the UI.
  async function moreReviewSuggestions(selection, catalog, suggestions, isCurrent) {
    const base = suggestions.find(suggestion => !suggestion.alternative && suggestion.changes?.length === 1 && confirmed(suggestion.validation));
    if (!base || suggestions.length >= 3 || !window.ElitePCPrivate?.fit || !isCurrent()) return [];
    const change = base.changes[0];
    // RAM/GPU alternatives must come from the server's specification-floor search.
    if (['ram','gpu'].includes(change.key)) return [];
    const used = new Set([selection.selections[change.key], ...suggestions.flatMap(suggestion => suggestion.changes.filter(part => part.key === change.key).map(part => part.option.sku))]);
    const candidates = (stockGroups(catalog).find(group => group.key === change.key)?.options || []).filter(option => !used.has(option.sku)).slice(0, 8);
    if (!candidates.length) return [];
    const fits = await window.ElitePCPrivate.fit({ ...selection, component: change.key, skus: candidates.map(option => option.sku) });
    if (!isCurrent()) return [];
    const matches = candidates.filter(option => fits.options?.some(fit => fit.sku === option.sku && fit.status === 'compatible')).slice(0, 3 - suggestions.length);
    const checked = await Promise.allSettled(matches.map(async option => {
      if (!isCurrent()) return null;
      const candidate = copySelection(selection);
      candidate.selections[change.key] = option.sku;
      const validationExpiresAt = Date.now() + 15000;
      const validation = await request('validate', candidate, 15000);
      return confirmed(validation) && isCurrent() ? { ...base, selection: candidate, validation, validationExpiresAt, changes: [{ ...change, option }] } : null;
    }));
    const failure = checked.find(result => result.status === 'rejected');
    if (failure && isCurrent()) console.warn('Additional replacement quote unavailable:', failure.reason?.message || 'Please retry.');
    return checked.flatMap(result => result.status === 'fulfilled' && result.value ? [result.value] : []);
  }

  async function suggest(host, { selection, catalog: suppliedCatalog, lockedKeys = [], issueKeys = [], force = false, currentPrice, productUrl, isCurrent = () => true, onSelect, onActivity = () => {}, presentation, createImage, onBrowse, autoSelect = false }) {
    if (!document.getElementById('elite-pc-repair-styles')) {
      const style = document.createElement('style'); style.id = 'elite-pc-repair-styles';
      style.textContent = '.elite-pc-repairs{margin:18px 0;color:#102136}.elite-pc-repairs h3{margin:0 0 8px;font-size:17px;color:#102136}.elite-pc-repairs p{margin:6px 0;font-size:14px;line-height:1.5;color:#102136}.elite-pc-repair{border:1px solid #c5d4e7;background:#f2f7ff;padding:14px;margin:12px 0;border-radius:8px}.elite-pc-repairs .elite-pc-repair strong{display:block;font-size:14px;color:#102136}.elite-pc-repair button{display:block;width:100%;padding:11px;margin-top:12px;background:#0866ff;color:#fff;border:0;border-radius:6px;font:inherit;font-size:14px;font-weight:700;cursor:pointer}.elite-pc-repair button:disabled{opacity:.55;cursor:wait}.elite-pc-repair button:focus-visible{outline:3px solid #102136;outline-offset:3px}' + `
        .elite-pc-repairs .elite-pc-repairs__status.is-checking{display:flex;align-items:center;gap:10px}
        .elite-pc-repairs__indicator{box-sizing:border-box;flex:0 0 18px;width:18px;height:18px;border:2px solid #d6e4ff;border-top-color:#0866ff;border-radius:50%;animation:elite-pc-repair-check 850ms linear infinite}
        @keyframes elite-pc-repair-check{to{transform:rotate(1turn)}}
        @media(prefers-reduced-motion:reduce){.elite-pc-repairs__indicator{animation:none}}
        .elite-pc-repairs__progress{position:relative;height:8px;margin:14px 0 18px;overflow:hidden;border-radius:4px;background:#e2e7f0}
        .elite-pc-repairs__progress[hidden]{display:none!important}
        .elite-pc-repairs__progress::before{content:'';position:absolute;inset:0 auto 0 0;width:35%;border-radius:inherit;background:#075cff;animation:elite-pc-repair-progress 1.4s ease-in-out infinite alternate}
        @keyframes elite-pc-repair-progress{from{transform:translateX(0)}to{transform:translateX(185.714%)}}
        @keyframes elite-pc-repair-pulse{from{opacity:.3}to{opacity:1}}
        @media(prefers-reduced-motion:reduce){.elite-pc-repairs__progress::before{left:32.5%;animation:elite-pc-repair-pulse 1.2s ease-in-out infinite alternate}}
        .elite-pc-repair dl{margin:10px 0;font-size:13px;line-height:1.5}
        .elite-pc-repair dl div{display:grid;grid-template-columns:34% minmax(0,1fr);gap:10px;padding:4px 0;border-bottom:1px solid #dce5f1}
        .elite-pc-repair dt{font-weight:700}.elite-pc-repair dd{margin:0;overflow-wrap:anywhere}
        .elite-pc-repair details{margin-top:12px}.elite-pc-repair summary{cursor:pointer;font-size:13px;font-weight:700}
        .elite-pc-repairs__link{display:block;padding:11px;margin-top:12px;background:#0866ff;color:#fff;text-align:center;font-size:14px;font-weight:700;text-decoration:none;border-radius:6px}
        .elite-pc-repairs__link:focus-visible{outline:3px solid #102136;outline-offset:3px}
        .elite-pc-repair .elite-pc-repair__previous{font-size:12px;color:#526078;margin-top:2px}
        .elite-pc-repair .elite-pc-repair__total{font-weight:700;color:#075cff}
      `;
      document.head.appendChild(style);
    }
    host.dataset.pcRepairStartedMs=String(Math.round(performance.now()));
    delete host.dataset.pcRepairReadyMs;
    host.classList.add('elite-pc-repairs');
    host.hidden = false;
    host.replaceChildren();
    host.setAttribute('aria-live', 'polite');
    const node = (tag, text) => { const el = document.createElement(tag); el.textContent = text; return el; };
    const keepingChoice = lockedKeys.length > 0;
    const cardLayout = presentation === 'cards' && !keepingChoice;
    if (cardLayout) host.dataset.pcRepairLayout = 'cards';
    else delete host.dataset.pcRepairLayout;
    const grid = node('div', '');
    grid.className = 'elite-pc-repairs__grid';
    const heading = node('h3', keepingChoice ? 'Suggested changes' : 'Compatible replacements');
    const status = node('p', keepingChoice ? 'Checking in-stock parts that work with your choice…' : 'Finding the cheapest compatible parts in stock…');
    status.className = 'elite-pc-repairs__status';
    const indicator = node('span', '');
    indicator.className = 'elite-pc-repairs__indicator';
    indicator.setAttribute('aria-hidden', 'true');
    const progress = node('div', '');
    progress.className = 'elite-pc-repairs__progress';
    progress.setAttribute('role', 'progressbar');
    progress.setAttribute('aria-label', 'Searching for compatible components');
    const setChecking = checking => {
      status.classList.toggle('is-checking', checking);
      status.setAttribute('aria-busy', String(checking));
      progress.hidden = !checking;
      status.hidden = cardLayout && !checking && grid.childElementCount > 0 && status.dataset.error !== 'true';
      // An older request must not clear a newer search's loading state.
      if (status.parentNode === host) host.setAttribute('aria-busy', String(checking));
      if (checking) status.prepend(indicator);
      else indicator.remove();
    };
    const reportActivity = (phase, suggestion) => {
      if (isCurrent()) onActivity({ phase, changes: suggestion?.changes || [] });
    };
    let applying = false;
    host.append(heading, status, progress);
    if (cardLayout) host.append(grid);
    setChecking(true);
    reportActivity('searching');
    const deadline = Date.now() + 6000;
    const remaining = limit => Math.max(0, Math.min(limit, deadline - Date.now()));
    const catalog = availableCatalog(suppliedCatalog, selection) || {componentGroups:[]};
    const browseReplacement = () => {
      const unavailable = catalog.componentGroups.filter(group => hardwareKeys.has(group.key)
        && selection.selections[group.key] && !inStock(group.options.find(option => option.sku === selection.selections[group.key]) || {}));
      const key = [...issueKeys, ...unavailable.map(group => group.key)].find(key => hardwareKeys.has(key));
      if (!cardLayout || !onBrowse || !key) return false;
      status.textContent = 'Choose an available replacement from the component options, or check again.';
      const label = catalog.componentGroups.find(group => group.key === key)?.label || 'component';
      const button = node('button', `Choose another ${label.toLowerCase()}`);
      button.type = 'button'; button.className = 'elite-pc-repairs__browse';
      button.addEventListener('click', () => { if (!applying && isCurrent()) onBrowse(key); });
      host.append(button);
      return true;
    };
    try {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 20000);
      let result;
      try {
        const candidateSkus = !keepingChoice ? candidates(catalog, selection, issueKeys) : undefined;
        const body = {...selection, lockedKeys, ...(candidateSkus ? {candidateSkus} : {}),
          ...(!keepingChoice && selection.accessories?.length ? {accessoryCandidates:accessoryCandidates(catalog,selection)} : {})};
        const preview = !force && window.ElitePCPrivate.peekRepairPreview?.(body);
        if (preview) {
          // Paint an exact-build suggestion synchronously. A preview never
          // extends its quote expiry; the Select handler checks expired quotes.
          result = preview;
          void window.ElitePCPrivate.repair(body).catch(() => {});
        } else result = await window.ElitePCPrivate.repair(body, {signal:controller.signal, force});
      }
      finally { clearTimeout(timer); }
      if (!isCurrent()) return;
      const suggestions = (result.suggestions || []).map(suggestion => ({ ...suggestion, validationExpiresAt: result.expiresAt }));
      host.dataset.pcRepairReadyMs=String(Math.round(performance.now()));
      if (suggestions.length) {
        heading.textContent = keepingChoice ? 'Suggested changes' : 'Compatible replacements';
        status.textContent = keepingChoice ? 'Keep your chosen part and apply one of these checked options.' : 'These replacements have been checked for compatibility and stock.';
      }
      if (!suggestions.length) {
        reportActivity('idle');
        if (keepingChoice) {
          status.textContent = 'We could not confirm an in-stock combination that keeps this part. Go back to choose another part, or try again.';
          return;
        }
        if (browseReplacement()) return;
        status.textContent = 'We could not find a checked replacement. Please check again or choose different components.';
        return;
      }
      const singleKey = suggestions.length && suggestions.every(suggestion => suggestion.changes.length === 1 && suggestion.changes[0].key === suggestions[0].changes[0].key) ? suggestions[0].changes[0].key : '';
      const partNames = { case: ['case', 'cases'], mobo: ['motherboard', 'motherboards'], cpu: ['processor', 'processors'], ram: ['memory kit', 'memory kits'], gpu: ['graphics card', 'graphics cards'], psu: ['power supply', 'power supplies'], cooler: ['cooler', 'coolers'], wifi: ['Wi-Fi adapter', 'Wi-Fi adapters'], sata_ssd: ['SATA SSD', 'SATA SSDs'], nvme_ssd: ['NVMe SSD', 'NVMe SSDs'], sata_hdd: ['hard drive', 'hard drives'] };
      if (cardLayout) heading.textContent = singleKey ? `Choose a compatible ${partNames[singleKey]?.[0] || 'replacement'}` : 'Choose compatible replacements';
      const automaticSelections = [];
      const renderSuggestion = suggestion => {
        const card = node('div', ''); card.className = 'elite-pc-repair';
        card.dataset.pcRepairComponents = suggestion.changes.map(change => change.key).join(' ');
        const verifiedSignature = selectionKey(suggestion.selection);
        const optionNames = [];
        const preview = () => { if (!applying) reportActivity('preview', suggestion); };
        card.addEventListener('pointerenter', preview);
        card.addEventListener('focusin', preview);
        let configurationDetails;
        if (cardLayout) {
          if (reusableSuggestion(suggestion, verifiedSignature, suggestion.validationExpiresAt)) {
            const stock = node('span', 'In stock'); stock.className = 'elite-pc-repair__stock'; card.append(stock);
          }
          for (const change of suggestion.changes) {
            const listed = catalog.componentGroups.find(group => group.key === change.key)?.options.find(option => option.sku === change.option.sku);
            const option = reviewOption(change.key, change.option, listed);
            optionNames.push(option.label);
            const part = node('div', ''); part.className = 'elite-pc-repair__part';
            const media = node('div', ''); media.className = 'elite-pc-repair__media';
            if (createImage) media.append(createImage(option));
            part.append(media, node('strong', option.label));
            const appendSpecs = (target, specs) => {
              const list = node('dl', ''); list.className = 'elite-pc-repair__specs';
              for (const spec of specs) {
                const row = node('div', ''); row.append(node('dt', spec.label), node('dd', spec.value)); list.append(row);
              }
              target.append(list);
            };
            appendSpecs(part, option.displaySpecs.slice(0, 6));
            if (option.displaySpecs.length > 6) {
              const more = node('details', ''); more.append(node('summary', 'More specifications'));
              appendSpecs(more, option.displaySpecs.slice(6)); part.append(more);
            }
            if (Number.isFinite(price(option))) { const amount = node('p', money.format(price(option))); amount.className = 'elite-pc-repair__part-price'; part.append(amount); }
            card.append(part);
          }
        } else if (keepingChoice) {
          for (const change of suggestion.changes) {
            const group = catalog.componentGroups.find(group => group.key === change.key);
            const previous = group?.options.find(option => option.sku === selection.selections[change.key]);
            const listed = group?.options.find(option => option.sku === change.option.sku);
            const option = { ...listed, ...change.option, images: listed?.images?.length ? listed.images : change.option.images, componentKey: change.key };
            const part = node('div', ''); part.className = 'elite-pc-repair__change';
            if (createImage) {
              const media = node('div', ''); media.className = 'elite-pc-repair__change-media';
              media.append(createImage(option)); part.append(media);
            }
            const copy = node('div', ''); copy.className = 'elite-pc-repair__change-copy';
            copy.append(node('strong', change.label || change.key));
            if (previous) {
              const old = node('p', `Replace: ${previous.label}`); old.className = 'elite-pc-repair__previous'; copy.append(old);
            }
            copy.append(node('p', `With: ${change.option.label}`));
            part.append(copy); card.append(part);
          }
        } else if (suggestion.alternative) {
          const overview = ['cpu', 'gpu', 'ram'].map(key => suggestion.parts.find(part => part.key === key)).filter(Boolean).map(partName).join(' · ');
          card.append(node('p', overview));
          configurationDetails = node('details', '');
          configurationDetails.append(node('summary', `View full configuration (${suggestion.parts.length} parts)`));
          const list = node('dl', '');
          for (const part of suggestion.parts) {
            const row = node('div', '');
            row.append(node('dt', part.label), node('dd', partName(part))); list.append(row);
          }
          configurationDetails.append(list);
        } else for (const change of suggestion.changes) {
          const name = ['cpu', 'mobo', 'ram', 'gpu', 'psu'].includes(change.key) ? change.option.filterLabel || change.option.label : change.option.label;
          card.append(node('strong', change.label || change.key), node('p', `${name} · ${money.format(price(change.option))}`));
        }
        if (!suggestion.validation) {
          const link = node('a', 'View this configuration');
          link.className = 'elite-pc-repairs__link';
          link.href = configurationUrl(suggestion.selection, productUrl, catalog) || '/collections/gaming-pcs';
          card.append(link);
          if (configurationDetails) card.append(configurationDetails);
          (cardLayout ? grid : host).append(card); return;
        }
        const freshQuote = reusableSuggestion(suggestion, verifiedSignature, suggestion.validationExpiresAt);
        const total = node('p', `${freshQuote ? 'Full PC price' : 'Last checked PC price'}: ${money.format(suggestion.validation.price.amount)}`);
        total.className = 'elite-pc-repair__total'; card.append(total);
        if (keepingChoice && Number.isFinite(currentPrice) && currentPrice > 0) {
          const difference = Math.round((suggestion.validation.price.amount - currentPrice) * 100) / 100;
          card.append(node('p', difference ? `${difference > 0 ? '+' : '−'}${money.format(Math.abs(difference))} from your current PC` : 'Same price as your current PC'));
        }
        const button = node('button', cardLayout ? 'Select' : keepingChoice ? 'Apply changes' : suggestion.alternative ? 'Use this configuration' : suggestion.changes.length === 1 ? `Select this ${suggestion.changes[0].label.toLowerCase()}` : 'Select these replacement parts');
        button.type = 'button'; button.dataset.pcRepairSelect = '';
        if (cardLayout) button.setAttribute('aria-label', `Select ${optionNames.join(' and ')}`);
        const selectSuggestion = async (automatic = false) => {
          if (!isCurrent() || applying) return false;
          applying = true;
          delete status.dataset.error;
          const buttons = host.querySelectorAll('button'); buttons.forEach(entry => { entry.disabled = true; });
          const reuse = reusableSuggestion(suggestion, verifiedSignature, suggestion.validationExpiresAt)
            && (!automatic || suggestion.validation.build?.allComponentsInStock === true);
          if (!reuse) {
            status.textContent = suggestion.alternative ? 'Refreshing this configuration and the full PC price…' : 'Refreshing this replacement and the full PC price…';
            progress.setAttribute('aria-label', 'Refreshing replacement compatibility, availability and price');
            setChecking(true);
          }
          reportActivity(reuse ? 'applying' : 'validating', suggestion);
          try {
            const validationExpiresAt = reuse ? suggestion.validationExpiresAt : Date.now() + 15000;
            const validation = reuse ? suggestion.validation : await request('validate', suggestion.selection, 15000);
            if (!isCurrent()) return false;
            if (!confirmed(validation) || (automatic && validation.build?.allComponentsInStock !== true)) throw new Error('This replacement is no longer available or compatible. Check again for current alternatives.');
            host.dataset.pcRepairAppliedMs = String(Math.round(performance.now()));
            await onSelect({ ...suggestion, validation, validationExpiresAt, catalog });
            return true;
          } catch (error) {
            if (!isCurrent()) return false;
            status.textContent = error.name === 'AbortError' ? 'This check took longer than expected. Please try again.' : error.message || 'Could not select this replacement. Please check again.';
            status.dataset.error = 'true';
            buttons.forEach(entry => { entry.disabled = false; });
            reportActivity('preview', suggestion);
            const url = configurationUrl(suggestion.selection, productUrl, catalog);
            if (!automatic && !keepingChoice && url && !card.querySelector('.elite-pc-repairs__link')) {
              const link = node('a', 'View this configuration');
              link.className = 'elite-pc-repairs__link'; link.href = url; card.append(link);
            }
          } finally {
            applying = false;
            setChecking(false);
          }
          return false;
        };
        button.addEventListener('click', () => { void selectSuggestion(); });
        if (confirmed(suggestion.validation) && suggestion.changes.length && suggestion.changes.every(change => hardwareKeys.has(change.key) || accessoryKinds.has(change.key))) automaticSelections.push(selectSuggestion);
        card.append(button);
        if (configurationDetails) card.append(configurationDetails);
        (cardLayout ? grid : host).append(card);
      };
      suggestions.forEach(renderSuggestion);
      if (autoSelect && !keepingChoice) {
        // Fix it applies the first checked in-stock repair. Expired previews
        // must pass a new stock/fit/price check before any selection changes.
        for (const select of automaticSelections.slice(0, 3)) {
          if (!isCurrent() || await select(true)) return;
        }
        return;
      }
      if (cardLayout && singleKey && onBrowse) {
        const browse = node('button', `View all ${partNames[singleKey]?.[1] || 'options'}  →`);
        browse.type = 'button'; browse.className = 'elite-pc-repairs__browse';
        browse.addEventListener('click', () => { if (!applying && isCurrent()) onBrowse(singleKey); });
        host.append(browse);
        // Paint the first confirmed repair immediately. Extra checked options
        // can arrive without delaying it or changing its focus/selection.
        void moreReviewSuggestions(selection, catalog, suggestions, () => isCurrent() && !applying)
          .then(extra => { if (isCurrent() && !applying) extra.forEach(renderSuggestion); })
          .catch(error => { if (isCurrent()) console.warn('Additional replacement options unavailable:', error?.message || 'Please retry.'); });
      }
      reportActivity('preview', suggestions[0]);
    } catch (error) {
      console.warn('PC replacement check could not finish:', error);
      if (isCurrent()) {
        reportActivity('idle');
        if (keepingChoice) {
          status.textContent = 'We could not check replacement parts just now. Go back and try Make compatible again.';
          return;
        }
        if (!browseReplacement()) status.textContent = 'We could not check replacements just now. Please check again or choose different components.';
      }
    } finally {
      setChecking(false);
    }
  }
  function compatibleCandidates(catalog, selection, lockedKeys = []) {
    if (!usableCatalog(catalog) || lockedKeys.length !== 1) return undefined;
    const locked = lockedKeys[0];
    const related = { cpu: ['mobo', 'ram', 'cooler', 'psu'], mobo: ['cpu', 'ram', 'cooler', 'case'],
      ram: ['mobo', 'cpu'], gpu: ['psu', 'case', 'mobo'], psu: ['gpu', 'cpu', 'mobo', 'case'],
      case: ['mobo', 'cooler', 'gpu', 'psu'], cooler: ['case', 'cpu', 'mobo'],
      nvme_ssd: ['mobo', 'cpu'], sata_ssd: ['mobo'], sata_hdd: ['case', 'mobo'], wifi: ['mobo'] }[locked];
    if (!related) return undefined;
    const groups = new Map(catalog.componentGroups.map(group => [group.key, group]));
    const selected = key => groups.get(key)?.options.find(option => option.sku === selection.selections[key]);
    const fact = (option, labels, pattern) => (option?.displaySpecs || []).filter(spec => labels.test(String(spec.label)))
      .map(spec => String(spec.value).toUpperCase().match(pattern)?.[1]?.replace(/\s/g, '')).find(Boolean);
    const socket = option => fact(option, /^(?:CPU )?Socket(?: Type)?$/i, /\b(AM[45]|LGA\s*\d{4})\b/);
    const memory = option => fact(option, /^(?:Generation|DDR Type|Memory Type|Memory Technology|RAM Type)$/i, /\b(DDR[345])\b/);
    const targetSocket = socket(selected(locked === 'mobo' ? 'mobo' : 'cpu'));
    const lockedMemory = locked === 'ram' ? memory(selected('ram')) : '';
    let targetMemory = memory(selected('mobo'));
    const capacity = option => Number((option?.displaySpecs || []).find(spec => /(?:total )?capacity(?: \(GB\))?$/i.test(spec.label))?.value?.replace?.(/\s*GB$/i,'') || 0);
    const minimumCapacity = capacity(selected('ram'));
    // A capacity-only RAM catalogue cannot identify the correct DDR platform.
    // Let the private search use its full records instead of guessing cheap RAM.
    if (related.includes('ram') && (groups.get('ram')?.options || []).some(option => inStock(option) && !memory(option))) return undefined;
    const queues = related.map(key => {
      const options = (groups.get(key)?.options || []).filter(option => option.sku !== selection.selections[key] && inStock(option));
      const ranked = options.filter(option => {
        // Public facts narrow the candidates only. Every resulting build is
        // checked privately for BIOS, fit, specification floors and stock.
        if (['cpu', 'mobo'].includes(key) && targetSocket && ['cpu', 'mobo'].includes(locked)
          && socket(option) && socket(option) !== targetSocket) return false;
        if (key === 'mobo' && lockedMemory && memory(option) && memory(option) !== lockedMemory) return false;
        if (key === 'ram' && targetMemory && memory(option) && memory(option) !== targetMemory) return false;
        if (key === 'ram' && minimumCapacity && capacity(option) < minimumCapacity) return false;
        if (key === 'cooler' && targetSocket) {
          const sockets = (option.displaySpecs || []).filter(spec=>/supported.*socket/i.test(spec.label)).map(spec=>String(spec.value).toUpperCase().replace(/\s/g,'')).join(',');
          if (sockets && !sockets.includes(targetSocket)) return false;
        }
        return true;
      }).sort((a, b) => (a.priceOrder || 0) - (b.priceOrder || 0) || price(a) - price(b) || a.sku.localeCompare(b.sku));
      if (key === 'mobo' && ['cpu', 'ram'].includes(locked) && ranked.length) targetMemory = memory(ranked[0]) || targetMemory;
      return ranked;
    });
    const candidates = [];
    // Spend most of the small request on the direct dependency (for example
    // motherboards for a CPU), rather than unrelated cooling/power variants.
    for (let index = 0; index < queues.length; index++) {
      for (let count = 0; count < ([2, 2, 1, 1][index] || 0) && queues[index].length; count++) {
        const option = queues[index].shift();
        if (!candidates.includes(option.sku)) candidates.push(option.sku);
      }
    }
    while (candidates.length < 6 && queues.some(queue => queue.length)) for (const queue of queues) {
      const option = queue.shift();
      if (option && !candidates.includes(option.sku)) candidates.push(option.sku);
      if (candidates.length === 6) break;
    }
    return candidates.length ? candidates : undefined;
  }
  function repairRequest({ catalog, selection, lockedKeys = [], issueKeys = [] }) {
    const keepingChoice = lockedKeys.length > 0;
    const candidateSkus = keepingChoice ? compatibleCandidates(catalog, selection, lockedKeys) : candidates(catalog, selection, issueKeys);
    const replacements = !keepingChoice && selection.accessories.length ? accessoryCandidates(catalog, selection) : undefined;
    return { ...copySelection(selection), lockedKeys: [...lockedKeys], ...(candidateSkus ? { candidateSkus } : {}),
      ...(replacements ? { accessoryCandidates: replacements } : {}) };
  }
  function eligibleRepairs(result, { selection, lockedKeys = [], issueKeys = [], component, repairMode }) {
    const keepingChoice = lockedKeys.length > 0;
    const accessories = lines => JSON.stringify((lines || []).map(line =>
      [line.component, line.sku, String(line.variantId || '')]).sort((a, b) => JSON.stringify(a).localeCompare(JSON.stringify(b))));
    const repairableAccessories = new Set(!keepingChoice ? issueKeys.filter(key => accessoryKinds.has(key)) : []);
    return (result.suggestions || []).filter(suggestion => {
      if (!suggestion.selection?.selections || !Array.isArray(suggestion.selection.accessories) || !Array.isArray(suggestion.changes)
        || !lockedKeys.every(key => suggestion.selection.selections[key] === selection.selections[key])) return false;
      if (repairMode) {
        const before = selection.selections, after = suggestion.selection.selections;
        if (!component || !before[component] || Object.keys(before).sort().join('|') !== Object.keys(after).sort().join('|')) return false;
        const different = Object.keys(before).filter(key => before[key] !== after[key]);
        if (repairMode === 'alternative' ? different.length !== 1 || different[0] !== component
          : !different.length || different.includes(component)) return false;
      }
      const changed = new Set(suggestion.changes.map(change => change.key));
      if ([...changed].some(key => !hardwareKeys.has(key) && !repairableAccessories.has(key))) return false;
      // A repair must not silently remove selected parts, warranties or accessories.
      if (Object.keys(selection.selections).some(key => !suggestion.selection.selections[key])) return false;
      if (Object.keys(suggestion.selection.selections).some(key => suggestion.selection.selections[key] !== selection.selections[key]
        && (!hardwareKeys.has(key) || !changed.has(key)))) return false;
      const untouched = lines => lines.filter(line => !repairableAccessories.has(line.component));
      if (accessories(untouched(suggestion.selection.accessories)) !== accessories(untouched(selection.accessories))) return false;
      for (const key of repairableAccessories) {
        const before = selection.accessories.filter(line => line.component === key);
        const after = suggestion.selection.accessories.filter(line => line.component === key);
        if (before.length !== after.length || (accessories(before) !== accessories(after) && !changed.has(key))) return false;
      }
      return true;
    });
  }
  function namedRepair(suggestion, catalog, expiresAt) {
    const changes = suggestion.changes.map(change => ({ ...change, option: reviewOption(change.key, change.option,
      catalog?.componentGroups?.find(group => group.key === change.key)?.options.find(option => option.sku === change.option.sku)) }));
    return { ...suggestion, changes, validationExpiresAt: expiresAt };
  }
  // A ready action reads only a fresh, fully checked answer. No preview,
  // fetch or delayed validation is allowed on the Fix it click path.
  function prepared(options, minimumRemainingMs = 0) {
    const body = options.requestBody || repairRequest(options), api = window.ElitePCPrivate;
    const full = { ...copySelection(options.selection), lockedKeys: body.lockedKeys,
      ...(body.accessoryCandidates ? { accessoryCandidates: body.accessoryCandidates } : {}) };
    const answers = [api?.peekRepair?.(body), body.candidateSkus ? api?.peekRepair?.(full) : null,
      api?.peekRepairForSelection?.(options.selection, minimumRemainingMs, body.lockedKeys)];
    for (const answer of answers) {
      if (!answer || answer.expiresAt <= Date.now() + minimumRemainingMs) continue;
      const suggestion = eligibleRepairs(answer, options).find(value => value.changes.length
        && reusableSuggestion(value, selectionKey(value.selection), answer.expiresAt)
        && value.validation.build?.allComponentsInStock === true);
      if (suggestion) return namedRepair(suggestion, options.catalog, answer.expiresAt);
    }
    return checkedAlternative(options, minimumRemainingMs);
  }
  function prepare(options) {
    const body = options.requestBody || repairRequest(options);
    if (checkedAlternative(options, 30000)?.checkedSource === 'prepared-catalogue') return body;
    window.ElitePCPrivate?.prepareRepair(options.selection, body.lockedKeys, body.candidateSkus, body.accessoryCandidates,
      { isCurrent: options.isCurrent, priority: options.priority || 0, fullSearchFallback: true });
    return body;
  }
  // These are distinct customer choices. Keeping the card's part must never
  // fall back to replacing it; replacing it must leave the other parts alone.
  function pickerRepairOptions(options) {
    const alternative = options.repairMode === 'alternative';
    const request = { ...options, repairMode: alternative ? 'alternative' : 'keep',
      lockedKeys: alternative ? Object.keys(options.selection.selections).filter(key => hardwareKeys.has(key) && key !== options.component) : [options.component],
      issueKeys: alternative ? [options.component] : [] };
    if (options.background) {
      // A failed browser shortlist can cause two complete server searches.
      // Start advance preparation with the authoritative catalogue once.
      request.requestBody = repairRequest(request);
      delete request.requestBody.candidateSkus;
    }
    return request;
  }
  function preparedPicker(options, minimumRemainingMs = 500) {
    const matrix=matrixPicker(options,minimumRemainingMs);
    if(matrix?.state==='ready')return matrix.repair;
    if(matrix?.state==='unavailable')return null;
    const request = pickerRepairOptions(options), repair = prepared(request, minimumRemainingMs);
    return repair ? { ...repair, usedAlternative: request.repairMode === 'alternative', repairMode: request.repairMode } : null;
  }
  function preparePicker(options) {
    const matrix=matrixPicker(options);
    if(matrix?.state==='ready'||matrix?.state==='unavailable')return matrix;
    const request = pickerRepairOptions(options);
    const ready = preparedPicker(options, 500);
    if (ready?.checkedSource === 'prepared-catalogue') return { state: 'ready', repair: ready };
    if(matrix)return matrix;
    prepare(request);
    const state = window.ElitePCPrivate?.repairPreparationState?.(options.selection, request.lockedKeys) || 'pending';
    return ready ? { state: 'ready', repair: ready } : { state: state === 'ready' ? 'pending' : state };
  }

  // A customer click must not wait for every card in the background matrix.
  // Search this exact action directly, retaining the same locks, stock checks
  // and original proof expiry used by the prepared path.
  async function resolvePicker(options) {
    const isCurrent = options.isCurrent || (() => true);
    if (!isCurrent()) return null;
    const ready = preparedPicker(options, 500);
    if (ready) return ready;
    if (matrixPicker(options)?.state === 'unavailable') return null;
    const query = pickerRepairOptions({ ...options, background: true });
    const api = window.ElitePCPrivate;
    if (!api?.repair) throw new Error('The compatibility service is unavailable. Please try again.');
    for (let attempt = 0; attempt < 2 && isCurrent(); attempt++) {
      const result = await api.repair(query.requestBody, { preparationPriority: 4, refresh: attempt > 0 });
      if (!isCurrent()) return null;
      const suggestions = eligibleRepairs(result, query);
      if (!suggestions.length) return null;
      const suggestion = suggestions.find(value => result.expiresAt > Date.now() + 500
        && reusableSuggestion(value, selectionKey(value.selection), result.expiresAt)
        && value.validation.build?.allComponentsInStock === true);
      if (suggestion) return { ...namedRepair(suggestion, options.catalog, result.expiresAt),
        usedAlternative: query.repairMode === 'alternative', repairMode: query.repairMode, checkedSource: 'requested-picker-repair' };
      // Slow transport can consume a short stock proof. Refresh the exact
      // returned plan once; never turn an expired answer into approval.
    }
    if (!isCurrent()) return null;
    throw new Error('The replacement could not be verified. Your current selections have been kept.');
  }

  // Direct picker action: return one checked build without creating another
  // dialog or exposing an unchecked suggestion as a successful configuration.
  async function apply({ catalog, selection, lockedKeys = [], issueKeys = [], isCurrent = () => true }) {
    const keepingChoice = lockedKeys.length > 0;
    const candidateSkus = keepingChoice ? compatibleCandidates(catalog, selection, lockedKeys) : candidates(catalog, selection, issueKeys);
    const replacementAccessories = !keepingChoice && selection.accessories.length ? accessoryCandidates(catalog, selection) : undefined;
    const body = { ...copySelection(selection), lockedKeys: [...lockedKeys], ...(candidateSkus ? { candidateSkus } : {}),
      ...(replacementAccessories ? { accessoryCandidates: replacementAccessories } : {}) };
    const api = window.ElitePCPrivate;
    const preview = api.peekRepairPreview?.(body);
    let result = api.peekRepair?.(body) || (preview && preview.repairVersion !== 3 ? preview : await api.repair(body));
    if (candidateSkus && !result.suggestions?.length && !result.searchedFullCatalogue && isCurrent()) {
      const full = { ...copySelection(selection), lockedKeys: [...lockedKeys],
        ...(replacementAccessories ? { accessoryCandidates: replacementAccessories } : {}) };
      result = api.peekRepair?.(full) || api.peekRepairPreview?.(full) || await api.repair(full);
    }
    if (!isCurrent()) return null;
    const possible = eligibleRepairs(result, { selection, lockedKeys, issueKeys });
    let failure;
    for (const suggestion of possible.slice(0, 3)) {
      if (!isCurrent()) return null;
      const signature = selectionKey(suggestion.selection);
      const reuse = reusableSuggestion(suggestion, signature, result.expiresAt)
        && suggestion.validation.build?.allComponentsInStock === true;
      try {
        const startedAt = Date.now();
        const validation = reuse ? suggestion.validation : await request('validate', suggestion.selection, 15000);
        if (!isCurrent()) return null;
        if (!confirmed(validation) || validation.build?.allComponentsInStock !== true) {
          throw new Error('The replacement is no longer in stock or compatible. Please try again.');
        }
        const changes = suggestion.changes.map(change => ({ ...change, option: reviewOption(change.key, change.option,
          catalog?.componentGroups?.find(group => group.key === change.key)?.options.find(option => option.sku === change.option.sku)) }));
        return { ...suggestion, changes, validation, validationExpiresAt: reuse ? result.expiresAt : startedAt + 15000 };
      } catch (error) {
        if (!isCurrent()) return null;
        failure = error;
      }
    }
    throw new Error(failure?.name === 'AbortError' ? 'The check took too long. Please try again.'
      : failure?.message || 'A checked replacement is not ready. Your current configuration has been kept.');
  }
  window.ElitePCRepairs = { suggest, apply, prepared, prepare, preparedPicker, resolvePicker, pickerPreparationState:matrixPicker, preparePicker, preparePickerBuild, preparedPickerFit, preparedPickerPrice, preparedPickerDisplayPrice, candidates, accessoryCandidates, compatibleCandidates };
  // Start from an explicit complete product link while the catalogue loads.
  // The normal configurator hook takes over with its exact current selection.
  const startInitialPicker=()=>{
  const initialSelection=initialPickerSelection();
  if(initialSelection)preparePickerBuild({selection:initialSelection,isCurrent:()=>{
    const current=initialPickerSelection();return current&&matrixKey(current)===matrixKey(initialSelection);
  }});
  };
  if(window.ElitePCData?.pending)void window.ElitePCData.ready.then(startInitialPicker);
  else startInitialPicker();
})();
